import { createClient } from '@supabase/supabase-js';
import { env } from '../../config/env';

const supabaseUrl = env.SUPABASE_URL;
const supabaseAnonKey = env.SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase environment variables are missing! Please check your .env file.');
  console.error('Required: REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY');
}

// Create base Supabase client
const baseSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});




// 세션 검증 함수 (순환 참조 방지를 위해 여기서 직접 구현)
const ensureValidSessionInternal = async () => {
  try {
    const { data: { session }, error } = await baseSupabase.auth.getSession();

    if (error || !session) {
      return null;
    }

    // 토큰 만료 시간 확인 (만료 5분 전에 갱신)
    const expiresAt = session.expires_at * 1000;
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (expiresAt - now < fiveMinutes) {
      console.log('⚡ 토큰 만료 임박, 자동 갱신 중...');
      const { data: refreshedSession, error: refreshError } = await baseSupabase.auth.refreshSession();

      if (refreshError || !refreshedSession.session) {
        console.error('❌ 토큰 갱신 실패:', refreshError);
        return null;
      }

      console.log('✅ 토큰 갱신 성공');
      return refreshedSession.session;
    }

    return session;
  } catch (error) {
    console.error('❌ 세션 확인 중 오류:', error);
    return null;
  }
};

// Proxy를 사용하여 Supabase 클라이언트의 from 메서드를 인터셉트
const createSupabaseProxy = (client) => {
  return new Proxy(client, {
    get(target, prop) {
      if (prop === 'from') {
        return (table) => {
          const queryBuilder = target.from(table);
          return wrapQueryBuilder(queryBuilder, table);
        };
      }
      return target[prop];
    }
  });
};

// QueryBuilder의 메서드들을 래핑
const wrapQueryBuilder = (queryBuilder, tableName) => {
  const QUERY_TIMEOUT = 10000; // 10초

  // 실제 쿼리를 실행하는 메서드들
  const executionMethods = ['then', 'execute'];

  return new Proxy(queryBuilder, {
    get(target, prop) {
      const original = target[prop];

      // then 메서드를 인터셉트하여 쿼리 실행 전 세션 체크 및 타임아웃 적용
      if (prop === 'then' && typeof original === 'function') {
        return async function (onFulfilled, onRejected) {
          try {
            // 1. 세션 유효성 확인
            const session = await ensureValidSessionInternal();
            if (!session) {
              console.warn(`⚠️ [${tableName}] 세션 없이 쿼리 실행`);
            }

            // 2. 타임아웃과 함께 쿼리 실행
            const queryPromise = original.call(target, onFulfilled, onRejected);
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error(`쿼리 타임아웃 [${tableName}]`)), QUERY_TIMEOUT)
            );

            return await Promise.race([queryPromise, timeoutPromise]);
          } catch (error) {
            // 타임아웃 에러 처리
            if (error.message?.includes('쿼리 타임아웃')) {
              console.error(`⏱️ ${error.message}`);
              // 타임아웃 알림은 한 번만 표시
              if (!window.__supabaseTimeoutShown) {
                window.__supabaseTimeoutShown = true;
                setTimeout(() => {
                  alert('요청 시간이 초과되었습니다. 페이지를 새로고침해주세요.');
                  window.__supabaseTimeoutShown = false;
                }, 100);
              }
            }

            // onRejected가 있으면 호출, 없으면 에러를 throw
            if (onRejected) {
              return onRejected(error);
            }
            throw error;
          }
        };
      }

      // 체이닝을 위해 다른 메서드들도 래핑
      if (typeof original === 'function') {
        return function (...args) {
          const result = original.apply(target, args);
          // 결과가 QueryBuilder라면 다시 래핑
          if (result && typeof result === 'object' && 'then' in result) {
            return wrapQueryBuilder(result, tableName);
          }
          return result;
        };
      }

      return original;
    }
  });
};

// Proxy로 래핑된 Supabase 클라이언트 생성
export const supabase = createSupabaseProxy(baseSupabase);

// Re-export from centralized location

export default supabase;